import React, {
  Dispatch,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Action, AnyAction } from "redux";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import {
  IonItem,
  IonFabButton,
  IonIcon,
  IonLabel,
  IonList,
  IonSpinner,
  IonAlert,
} from "@ionic/react";
import {
  locate,
  trash,
  timeSharp,
  carSportSharp,
  chevronDown,
  location,
  time,
} from "ionicons/icons";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import "dayjs/locale/de";
import "dayjs/locale/en";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import "./BottomContainer.scss";
import { RootState, actions } from "../../store";
import TariffsCarousel from "../TariffsCarousel/TariffsCarousel";
import {
  CalculateCostInfo,
  IRoutingParams,
} from "../../webServer/controllers/customer/types/CalculateCost";
import Additionally from "../Additionally/Additionally";
import WebServerApi from "../api/WebServerApi";
import SearchAdress from "../SearchAdress/SearchAdress";
import TAXI_PARAMS from "../../utils/build";
import { AddressCenter } from "sedi-webserverproxy";
import { IRoute } from "../../store/map/types";
import { routePoints } from "../../utils/routePoints";

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps> & {
    handleLocationClick: () => void;
  };

interface OrderData {
  id: number;
  status: {
    id: string;
    name: string;
  };
  date: Date;
  tariff: string;
  paymentManner: string;
  routeForm: string;
  routeTo: string;
  driver: {
    name: string;
    number: string;
    carModel: string;
    color: string;
  };
}

const BottomContainer: React.FC<Props> = (props) => {
  const {
    handleLocationClick,
    calculateCostAction,
    footerHeightAction,
    route,
    selectedPointAction,
    valueFromViewAction,
    successAddOrder,
    successAddOrderAction,
    getOrignAction,
    getDestinationAction,
    routeResetAction,
    routeReset,
    serviceIsChecked,
    lang,
    carInWayAction,
    center,
    activationKeyResult,
    routingInfo,
    getRoutingInfoAction,
    getRouteAction,
    activationKeyOpenAction,
    currentTariffOpenAction,
    calcCostProcessingAction,
  } = props;

  const history = useHistory();
  const webServerApi = useMemo(() => new WebServerApi(), []);
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  const [searchOpen, setSearchOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [loader, setLoader] = useState(false);
  const [orders, setOrders] = useState<OrderData | null>(null);

  const translations = useMemo(
    () => ({
      fromWhere: t("textField.fromWhere"),
      where: t("textField.where"),
      deleteText: t("deleteText"),
      no: t("no"),
      yes: t("yes"),
      notSpecified: t("notSpecified"),
    }),
    [t]
  );

  const handleSearchToggle = useCallback(
    (selectedPoint: number, selectedAddress: any) => {
      valueFromViewAction(selectedAddress);
      selectedPointAction(selectedPoint);
      setSearchOpen(true);
    },
    [valueFromViewAction, selectedPointAction]
  );

  const closeSearchClick = useCallback(() => {
    setSearchOpen(false);
  }, []);

  const handleAlertDismiss = useCallback(() => {
    setShowAlert(false);
  }, []);

  useEffect(() => {
    if (!route.point1) return;

    const hasValidRoutingInfo =
      routingInfo &&
      routingInfo.from?.latitude === route.point1?.g.lat &&
      routingInfo.from?.longitude === route.point1?.g.lon &&
      routingInfo.to?.latitude === route.point2?.g.lat &&
      routingInfo.to?.longitude === route.point2?.g.lon;

    const parameter: CalculateCostInfo = {
      licenseKey: TAXI_PARAMS.WEBSERVER_APIKEY || "",
      userKey: activationKeyResult?.userKey,
      orderTime: new Date(),
      totalDuration: hasValidRoutingInfo ? routingInfo!.totalDuration : 0,
      totalDistance: hasValidRoutingInfo ? routingInfo!.totalDistance : 0,
      specialServices: serviceIsChecked,
      isCashless: true,
      routePoints: [
        {
          latitude: route.point1.g.lat > 0 ? route.point1.g.lat : center.lat,
          longitude: route.point1.g.lon > 0 ? route.point1.g.lon : center.lon,
        },
        ...(hasValidRoutingInfo ? routingInfo!.routePoints : []),
        ...(route.point2
          ? [
              {
                latitude: route.point2.g.lat,
                longitude: route.point2.g.lon,
              },
            ]
          : []),
      ],
    };

    calcCostProcessingAction(true);
    calculateCostAction(parameter).finally(() => {
      calcCostProcessingAction(false);
    });
  }, [
    routingInfo,
    serviceIsChecked,
    route.point1,
    route.point2,
    center,
    activationKeyResult,
    calculateCostAction,
    calcCostProcessingAction,
  ]);

  const calculateCostParameters = useCallback((): CalculateCostInfo | null => {
    if (!route.point1) return null;

    const baseRoutePoints = [
      {
        latitude: route.point1.g.lat > 0 ? route.point1.g.lat : center.lat,
        longitude: route.point1.g.lon > 0 ? route.point1.g.lon : center.lon,
      },
    ];

    let totalDuration = 0;
    let totalDistance = 0;
    let additionalRoutePoints: any[] = [];

    if (
      routingInfo &&
      route.point2 &&
      routingInfo.from?.latitude === route.point1.g.lat &&
      routingInfo.from?.longitude === route.point1.g.lon &&
      routingInfo.to?.latitude === route.point2.g.lat &&
      routingInfo.to?.longitude === route.point2.g.lon
    ) {
      totalDuration = routingInfo.totalDuration;
      totalDistance = routingInfo.totalDistance;
      additionalRoutePoints = routingInfo.routePoints;
    }

    const finalRoutePoints = [
      ...baseRoutePoints,
      ...additionalRoutePoints,
      ...(route.point2
        ? [
            {
              latitude: route.point2.g.lat,
              longitude: route.point2.g.lon,
            },
          ]
        : []),
    ];

    return {
      licenseKey: TAXI_PARAMS.WEBSERVER_APIKEY || "",
      userKey: activationKeyResult?.userKey,
      orderTime: new Date(),
      totalDuration,
      totalDistance,
      specialServices: serviceIsChecked,
      isCashless: true,
      routePoints: finalRoutePoints,
    };
  }, [
    route.point1,
    route.point2,
    center,
    routingInfo,
    serviceIsChecked,
    activationKeyResult,
  ]);

  useEffect(() => {
    const parameters = calculateCostParameters();
    if (!parameters) return;

    calcCostProcessingAction(true);
    calculateCostAction(parameters).finally(() => {
      calcCostProcessingAction(false);
    });
  }, [calculateCostParameters, calculateCostAction, calcCostProcessingAction]);

  const getOrdersData = useCallback(
    (data: any) => {
      if (!data.Success || !data.Orders?.[0]) return;

      const order = data.Orders[0];
      const { ID, Status, Date, Tariff, PaymentManner, Route, Driver } = order;

      const carModel =
        Driver?.Car?.Props?.Properties?.find(
          (item: any) => item.Key === "CarModel"
        )?.Value.Name || translations.notSpecified;

      const color =
        Driver?.Car?.Props?.Properties?.find(
          (item: any) => item.Key === "Color"
        )?.Value.Name || translations.notSpecified;

      const formatAddress = (point: any) =>
        `${point.StreetName} ${point.HouseNumber || point.ObjectName || ""}`.trim();

      const routeForm = formatAddress(Route.Points[0]);
      const routeTo = Route.Points[1] ? formatAddress(Route.Points[1]) : "";

      const orderData: OrderData = {
        id: ID,
        status: {
          id: Status.ID,
          name: Status.Name,
        },
        date: new Date(Date),
        tariff: Tariff.Name,
        paymentManner: PaymentManner,
        routeForm,
        routeTo,
        driver: {
          name: Driver.Name,
          number: Driver.Car?.Number || "",
          carModel,
          color,
        },
      };

      setOrders(orderData);

      const routeFrom = {
        lat: Route.Points[0].GeoPoint.Lat,
        lon: Route.Points[0].GeoPoint.Lon,
      };
      getOrignAction(routeFrom);

      if (Driver.Geo) {
        const routeTo = {
          lat: Driver.Geo.Lat,
          lon: Driver.Geo.Lon,
        };
        getDestinationAction(routeTo);
      }
    },
    [getOrignAction, getDestinationAction, translations.notSpecified]
  );

  const handleNoOrders = useCallback(() => {
    if (history.location.pathname === "/authorization") {
      history.push("/");
      activationKeyOpenAction(false);
      currentTariffOpenAction(true);
    } else {
      routeResetAction(!routeReset);
    }
  }, [
    history,
    activationKeyOpenAction,
    currentTariffOpenAction,
    routeResetAction,
    routeReset,
  ]);

  useEffect(() => {
    if (!activationKeyResult?.userKey) return;

    webServerApi.getOrders(activationKeyResult.userKey, lang).then((data) => {
      getOrdersData(data);

      if (data.Success) {
        if (data.Orders[0]) {
          successAddOrderAction(true);
          carInWayAction(data.Orders[0].Status.ID === "inway");
        } else {
          handleNoOrders();
        }
      }
    });
  }, [
    successAddOrder,
    activationKeyResult,
    lang,
    webServerApi,
    getOrdersData,
    successAddOrderAction,
    carInWayAction,
    handleNoOrders,
  ]);

  useEffect(() => {
    if (!activationKeyResult?.userKey) return;

    const timer = setInterval(() => {
      webServerApi.getOrders(activationKeyResult.userKey, lang).then((data) => {
        getOrdersData(data);

        if (data.Success) {
          if (data.Orders[0]) {
            carInWayAction(data.Orders[0].Status.ID === "inway");
          }
          if (data.Orders.length === 0) {
            successAddOrderAction(false);
            clearInterval(timer);
            setLoader(false);
          }
        } else {
          successAddOrderAction(false);
          clearInterval(timer);
        }
      });
    }, 5000);

    return () => clearInterval(timer);
  }, [
    activationKeyResult,
    lang,
    webServerApi,
    getOrdersData,
    carInWayAction,
    successAddOrderAction,
  ]);

  const getStatuses = useCallback(() => {
    if (!orders) return null;

    if (orders.status.id === "search") {
      return (
        <>
          <IonSpinner slot="start" name="lines" color="primary" />
          <ul className="driverRow">
            <li>{orders.status.name}</li>
          </ul>
        </>
      );
    }

    return (
      <>
        <IonIcon icon={carSportSharp} slot="start" />
        <ul className="driverRow">
          <li>
            {orders.driver.number}, {orders.driver.carModel},{" "}
            {orders.driver.color}, {orders.driver.name}
          </li>
        </ul>
      </>
    );
  }, [orders]);

  useEffect(() => {
    if (ref.current) {
      footerHeightAction(ref.current.clientHeight);
    }
  }, [footerHeightAction]);

  const pointAB = useCallback(() => {
    if (!route.point1 || !route.point2) return;

    const pointA: AddressCenter = {
      lat: route.point1.g.lat,
      lon: route.point1.g.lon,
    };

    const pointB: AddressCenter = {
      lat: route.point2.g.lat,
      lon: route.point2.g.lon,
    };

    if (route.point1.g.lat > 0 || route.point2.g.lat > 0) {
      getOrignAction(pointA);
      getDestinationAction(pointB);
    }
  }, [route.point1, route.point2, getOrignAction, getDestinationAction]);

  useEffect(() => {
    pointAB();
  }, [pointAB]);

  const handleCancelOrder = useCallback(() => {
    if (!activationKeyResult || !orders) return;

    webServerApi
      .cancelOrder(orders.id, activationKeyResult.userKey, lang)
      .then(() => {
        setLoader(true);
        getRoutingInfoAction(null);
        getRouteAction({ point1: null, point2: null });
        valueFromViewAction("");
      });
  }, [
    activationKeyResult,
    orders,
    webServerApi,
    lang,
    getRoutingInfoAction,
    getRouteAction,
    valueFromViewAction,
  ]);

  const routePoint1 = useMemo(() => routePoints(route.point1), [route.point1]);
  const routePoint2 = useMemo(() => routePoints(route.point2), [route.point2]);

  return (
    <div className="ionPage" ref={ref}>
      <IonFabButton
        color="danger"
        size="small"
        onClick={handleLocationClick}
        className="ionFabButton"
      >
        <IonIcon icon={locate} />
      </IonFabButton>

      {routingInfo && routingInfo.totalDistance !== 0 && (
        <div className="legsInfo">
          <div className="legsInfo__item">
            <IonIcon icon={location} />
            <span>{routingInfo.totalDistance} km</span>
          </div>
          <div className="legsInfo__item">
            <IonIcon icon={time} />
            <span>{routingInfo.totalDuration} min</span>
          </div>
        </div>
      )}

      {successAddOrder && orders ? (
        <IonList className="orderList">
          <div className="orderDelete" onClick={() => setShowAlert(true)}>
            {loader ? (
              <IonSpinner name="lines" color="primary" />
            ) : (
              <IonIcon icon={trash} />
            )}
            <IonLabel>{orders.status.name}</IonLabel>
            <IonIcon className="down" icon={chevronDown} />
          </div>

          <div className="getOrders">
            <div className="getOrders__item">
              <span className="inputIcon">A</span>
              <IonLabel>{orders.routeForm}</IonLabel>
            </div>

            {orders.routeTo && (
              <div className="getOrders__item">
                <span className="inputIcon">B</span>
                <IonLabel>{orders.routeTo}</IonLabel>
              </div>
            )}

            <div className="getOrders__item">
              <IonIcon icon={timeSharp} />
              <IonLabel dir="ltr">
                {dayjs(orders.date)
                  .locale(lang || "en")
                  .format("DD MMMM HH:mm")}
              </IonLabel>
              <IonLabel>ID: #{orders.id}</IonLabel>
            </div>
          </div>

          <div className="orderDriver">{getStatuses()}</div>
        </IonList>
      ) : (
        <>
          <IonItem onClick={() => handleSearchToggle(1, routePoint1)}>
            <span className="inputIcon">A</span>
            <IonLabel>{routePoint1 || translations.fromWhere}</IonLabel>
          </IonItem>
          <IonItem onClick={() => handleSearchToggle(2, routePoint2)}>
            <span className="inputIcon">B</span>
            <IonLabel>{routePoint2 || translations.where}</IonLabel>
          </IonItem>

          <Additionally />
          <TariffsCarousel />
        </>
      )}

      <IonAlert
        isOpen={showAlert}
        onDidDismiss={handleAlertDismiss}
        header={`ID: #${orders?.id || ""}`}
        message={translations.deleteText}
        buttons={[
          {
            text: translations.no,
            role: "cancel",
            cssClass: "secondary",
            handler: handleAlertDismiss,
          },
          {
            text: translations.yes,
            handler: handleCancelOrder,
          },
        ]}
      />

      <SearchAdress searchOpen={searchOpen} handleSearchToggle={closeSearchClick} />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  valueFromView: state.map.valueFromView,
  cost: state.map.cost,
  center: state.map.center,
  route: state.map.route,
  routingInfo: state.map.routingInfo,
  successAddOrder: state.map.successAddOrder,
  destination: state.map.destination,
  routeReset: state.map.routeReset,
  serviceIsChecked: state.map.serviceIsChecked,
  lang: state.map.lang,
  activationKeyResult: state.registration.activationKeyResult,
});

const mapDispatchToProps = (
  dispatch: Dispatch<Action> & ThunkDispatch<any, any, AnyAction>
) => ({
  valueFromViewAction: (valueFromView: string) =>
    dispatch(actions.map.valueFromViewAction(valueFromView)),
  calculateCostAction: (parameter: CalculateCostInfo) =>
    dispatch(actions.map.calculateCostAction(parameter)),
  footerHeightAction: (footerHeight: number) =>
    dispatch(actions.map.footerHeightAction(footerHeight)),
  selectedPointAction: (selectedPoint: number) =>
    dispatch(actions.map.selectedPointAction(selectedPoint)),
  setPointAutocomplateAction: (changePointAutocomplate: boolean) =>
    dispatch(actions.map.setPointAutocomplateAction(changePointAutocomplate)),
  successAddOrderAction: (successAddOrder: boolean) =>
    dispatch(actions.map.successAddOrderAction(successAddOrder)),
  getOrignAction: (orign: AddressCenter) =>
    dispatch(actions.map.getOrignAction(orign)),
  getDestinationAction: (destination: AddressCenter) =>
    dispatch(actions.map.getDestinationAction(destination)),
  routeResetAction: (routeReset: boolean) =>
    dispatch(actions.map.routeResetAction(routeReset)),
  getRoutingInfoAction: (parameter: IRoutingParams | null) =>
    dispatch(actions.map.getRoutingInfoAction(parameter)),
  carInWayAction: (carInWay: boolean) =>
    dispatch(actions.map.carInWayAction(carInWay)),
  getRouteAction: (route: IRoute) =>
    dispatch(actions.map.getRouteAction(route)),
  activationKeyOpenAction: (activationKeyOpen: boolean) =>
    dispatch(
      actions.registration.activationKeyOpenAction(activationKeyOpen)
    ),
  currentTariffOpenAction: (currentTariffOpen: boolean) =>
    dispatch(
      actions.registration.currentTariffOpenAction(currentTariffOpen)
    ),
  calcCostProcessingAction: (calcCostProcessing: boolean) =>
    dispatch(
      actions.registration.calcCostProcessingAction(calcCostProcessing)
    ),
});

export default React.memo(
  connect(mapStateToProps, mapDispatchToProps)(BottomContainer)
);
